import React, { useEffect, useState } from "react";
import { Button } from "antd";
import { Pagination } from "antd";
import { useSelector } from "react-redux";

import GroupIcon from "../../assets/icons/Group.svg";
import { HiOutlineCheckCircle } from "react-icons/hi2";

import { getAllSupportRequests, updateSupportRequestStatus } from "../../store";
import { useThunk } from "../../hooks/useThunk";

import SupportRequestDetailsModal from "../../components/Model/SupportRequestDetailsModal";
import { useConfirmation } from "../../hooks/useConfirmation";
import { SupportLogsData } from "../../components/Data/landlordPayouts";
import {
  supportRequestTypes,
  supportRequestStatus,
} from "../../constants/commonConstants";
import TableHeader from "../../components/Common/TableHeader";
import Table from "../../components/Table/LandLordTable";
import NotFound from "../../components/NotFound";
import DetailPopover from "../../components/Common/DetailPopover";
import showToast from "../../utils/toastNotifications";
import Loader from "../../components/Common/Loader";

const SupportLogs = () => {
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [selectedRange, setSelectedRange] = useState(""); // Used for dropdown-based date ranges

  // pagination
  const ITEMS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);

  const supportLogs = useSelector((state) => state.supportRequest.supportLogs.logs);
  const pagination = useSelector((state) => state.supportRequest.supportLogs.pagination);

  const [doGetSupportRequests, isGetting, supportError] = useThunk(getAllSupportRequests);
  const [doUpdateSupportRequest, isUpdating, updateError] = useThunk(updateSupportRequestStatus);

  const loadSupportRequests = async () => {

    let filteredReqType = null;
    if (type == supportRequestTypes.LEGAL) {
      filteredReqType = "LEGAL";
    } else if (type == supportRequestTypes.TAX) {
      filteredReqType = "TAX_CONSULT";
    } else if (type == supportRequestTypes.MAINTENANCE) {
      filteredReqType = "MAINTENANCE";
    } else {
      filteredReqType = null;
    }

    let startDate = null;
    let endDate = null;
    const now = new Date();

    if (selectedRange === "today") {
      startDate = new Date(now.setHours(0, 0, 0, 0));
      endDate = new Date(now.setHours(23, 59, 59, 999));
    } else if (selectedRange === "this_week") {
      // Week starts on Sunday
      const firstDayOfWeek = new Date(now);
      firstDayOfWeek.setDate(now.getDate() - now.getDay());
      firstDayOfWeek.setHours(0, 0, 0, 0);
      const lastDayOfWeek = new Date(firstDayOfWeek);
      lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
      lastDayOfWeek.setHours(23, 59, 59, 999);
      startDate = firstDayOfWeek;
      endDate = lastDayOfWeek;
    } else if (selectedRange === "this_month") {
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      firstDayOfMonth.setHours(0, 0, 0, 0);
      lastDayOfMonth.setHours(23, 59, 59, 999);
      startDate = firstDayOfMonth;
      endDate = lastDayOfMonth;
    } else if (selectedRange === "last_month") {
      const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      firstDayOfLastMonth.setHours(0, 0, 0, 0);
      lastDayOfLastMonth.setHours(23, 59, 59, 999);
      startDate = firstDayOfLastMonth;
      endDate = lastDayOfLastMonth;
    } else {
      startDate = null;
      endDate = null;
    }

    let landlordName = null;
    let unitId = null;
    if (search) {
      // Check if search matches UNIT- followed by 1+ digits (case-insensitive)
      if (/^UNIT-\d+$/i.test(search.trim().toUpperCase())) {
        unitId = search.trim().toUpperCase();
      } else {
        landlordName = search.trim();
      }
    }

    const offset = (currentPage - 1) * ITEMS_PER_PAGE;

    const params = {
      landlordName: landlordName,
      unitId: unitId,
      requestType: filteredReqType,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      offset: offset,
      limit: ITEMS_PER_PAGE,
    };

    const result = await doGetSupportRequests(params);
    if (result?.success) {
      // console.log("Support requests loaded successfully");
    } else {
      console.error("Failed to load support requests:", result.error.message);
    }
  };

  useEffect(() => {
    loadSupportRequests();
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  }

  // for test
  const handleSearch = () => {
    loadSupportRequests();
  };

  const markRequestAsCompleted = async (record) => {
    const supportRequestId = record.details[0]?._id
    // console.log("Updating support request status for:", supportRequestId);
    const result = await doUpdateSupportRequest(supportRequestId);
    if (result?.success) {

      showToast("success", "Support request marked as completed");
    } else {
      showToast("error", `Failed to mark request as completed: ${result.error.message}`);
    }
  }

  // importing the custom hook properties
  const { requestConfirmation, confirmation, confirm, cancel } =
    useConfirmation();

  const handleOnClick = async (record) => {
    setSelectedRecord(record);
    // Trigger the confirmation modal
    const isConfirmed = await requestConfirmation();

    if (isConfirmed) {
      // If confirmed, proceed with the action
      markRequestAsCompleted(record);
    } else {
      // If cancelled, handle accordingly
    }
  };

  const handleComplete = (record) => {
    markRequestAsCompleted(record);
    // logic to set status as complete - integration with backend can be done here
  };

  const columns = [
    {
      title: "Date / Time",
      dataIndex: "date",
      key: "date",
      render: (_, record) => `${record.date} ${record.time}`,
    },
    {
      title: "LandLord Name",
      dataIndex: "landlordName",
      key: "landlordName",
    },
    {
      title: "Contact Info",
      dataIndex: "contactInfo",
      key: "contactInfo",
    },
    {
      title: "Actions",
      key: "action",
      render: (record) => (
        <div className="flex justify-center gap-2 sm:justify-start">
          <button
            onClick={() => handleOnClick(record)} // Use onViewUnits in the button click handler
            className="flex justify-center items-center gap-1 text-[.75rem] rounded-[15px] w-[75px] h-[28px] border"
            style={{ color: "#2EA83C", borderColor: "#07C01D" }}
          >
            <img src={GroupIcon} alt="Group icon" width={14} height={14} />
            View
          </button>
          {!(record.status === supportRequestStatus.COMPLETED) && (
            <Button
              onClick={() => handleComplete(record)}
              icon={<HiOutlineCheckCircle size={14} />}
              className="border-orange-action capitalize text-orange-action text-xs leading-6 rounded-[15px] h-[28px] px-3 flex justify-center items-center gap-1 text-[.75rem] w-[102px] border "
            >
              complete
            </Button>
          )}
        </div>
      ),
    },
  ];

  const subTableConfig = {
    dataField: "details",
    columns: [
      {
        title: (
          <span className="text-[#4772F1] font-bold font-inter text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">
            Request Type
          </span>
        ),
        dataIndex: "requestType",
        key: "requestType",
        render: (requestType) => (
          <span
            className={`capitalize font-inter text-[9px] sm:text-[10px] md:text-[11px] lg:text-[12px] leading-[20px] ${requestType === supportRequestTypes.MAINTENANCE
              ? "text-orange-action"
              : requestType === supportRequestTypes.LEGAL
                ? "text-[#4772F1]"
                : requestType === supportRequestTypes.TAX
                  ? "text-green-action"
                  : ""
              }`}
          >
            {requestType}
          </span>
        ),
      },
      {
        title: (
          <span className="text-[#4772F1] font-bold font-inter text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">
            Related Unit/Tenant
          </span>
        ),
        dataIndex: "relatedUnit",
        key: "relatedUnit",
        // responsive: ["sm"],
      },
      {
        title: (
          <span className="text-[#4772F1] font-bold font-inter text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">
            Preview
          </span>
        ),
        dataIndex: "subject",
        key: "subject",
        // responsive: ["sm"],
        render: (subject, record) => (
          <DetailPopover title={record.subject} text={record.description}>
            {" "}
            {subject}{" "}
          </DetailPopover>
        ),
      },
      {
        title: (
          <span className="text-[#4772F1] font-bold font-inter text-[8px] sm:text-[10px] md:text-[12px] lg:text-[14px]">
            Status
          </span>
        ),
        dataIndex: "status",
        key: "status",
        render: (status) => {
          // Convert status like "IN_PROGRESS" to "In Progress"
          const displayStatus = status
            .toLowerCase()
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          return (
            <span
              className={`font-inter text-[9px] capitalize sm:text-[10px] md:text-[11px] lg:text-[12px] leading-[20px] ${status === supportRequestStatus.IN_PROGRESS
                ? "text-orange-action"
                : status === supportRequestStatus.NEW
                  ? "text-[#4772F1]"
                  : status === supportRequestStatus.COMPLETED
                    ? "text-green-action"
                    : ""
                }`}
            >
              {displayStatus}
            </span>
          );
        },
      },
    ],
  };

  // const TableData = SupportLogsData.map((item, index) => ({
  //   key: `support-${index}`,
  //   date: item.date,
  //   time: item.time,
  //   landlordName: item.landlordName,
  //   contactInfo: item.contactInfo,
  //   requestType: item.requestType,
  //   relatedUnit: item.relatedUnit,
  //   subject: item.subject,
  //   status: item.status,
  //   description: item.description,
  //   contact: item.contact,
  //   unitId: item.unitId,
  //   tenantName: item.tenantName,
  //   submitDate: item.submitDate,
  //   landlordDate: item.landlordDate,
  //   details: [item],
  // }));

  const TableData = supportLogs.map((item, index) => {
    const createdDate = item.createdAt ? new Date(item.createdAt) : null;
    const landlord = item.landlord || {};
    const contactObj = item.isDeleted ? {
      email: "record deleted",
      phone: "",
    } : {
      email: landlord.email || "",
      phone: landlord.phone || "",
    };
    // Format date as MM/DD
    const formatShortDate = (date) =>
      date
        ? `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
          date.getDate()
        ).padStart(2, "0")}`
        : "";
    // Format time as HH:mm
    const formatShortTime = (date) =>
      date
        ? `${String(date.getHours()).padStart(2, "0")}:${String(
          date.getMinutes()
        ).padStart(2, "0")}`
        : "";

    let subject = "";
    let supportRequestType = "";
    if (item.requestType === "TAX_CONSULT") {
      subject = "Tax Consultation Request";
      supportRequestType = supportRequestTypes.TAX;
    } else if (item.requestType === "MAINTENANCE") {
      subject = "Maintenance Support Request";
      supportRequestType = supportRequestTypes.MAINTENANCE;
    } else if (item.requestType === "LEGAL") {
      subject = "Legal Support Request";
      supportRequestType = supportRequestTypes.LEGAL;
    }

    let status;
    switch (item.status) {
      case "PENDING":
        status = supportRequestStatus.IN_PROGRESS;
        break;
      case "COMPLETED":
        status = supportRequestStatus.COMPLETED;
        break;
      default:
        status = status; // fallback to original if not matched
    }

    const newItem = { ...item }
    newItem.subject = subject;
    newItem.requestType = supportRequestType;
    newItem.relatedUnit = item.unitId || "";
    newItem.status = status;

    return {
      key: `${index + 1}`,
      date: formatShortDate(createdDate), // e.g., "06/20"
      time: formatShortTime(createdDate), // e.g., "06:08"
      landlordName: landlord.fullName || "",
      contactInfo: newItem.isDeleted ? "record deleted" : [landlord.email, landlord.phone].filter(Boolean).join(" / "),
      status: newItem.status,
      relatedUnit: newItem.relatedUnit || "",
      subject: newItem.subject || "",
      submitDate: createdDate ? createdDate.toLocaleString() : "",
      landlordDate: createdDate ? createdDate.toLocaleString() : "",
      requestType: newItem.requestType || "",
      contact: contactObj,
      unitId: item.unitId || "",
      tenantName: item.tenantName || "",
      description: item.description || "",
      details: [newItem],
    };
  });

  return (
    <div className="bg-[#F5F7FA] p-4 sm:p-6 md:p-[30px]">
      <div className="">
        {/* Search bar */}
        <TableHeader
          searchValue={search}
          onSearchChange={setSearch}
          onSearchSubmit={handleSearch}
          searchPlaceholder="Search by landlord name, unit ID"
          filters={[
            {
              name: "Type",
              value: type,
              onChange: setType,
              options: [
                { label: "All", value: "" },
                { label: supportRequestTypes.MAINTENANCE, value: supportRequestTypes.MAINTENANCE },
                { label: supportRequestTypes.LEGAL, value: supportRequestTypes.LEGAL },
                { label: supportRequestTypes.TAX, value: supportRequestTypes.TAX },
              ],
            },
          ]}
          dateRangeFilter={{
            value: selectedRange,
            onChange: setSelectedRange,
            options: [
              { label: "Today", value: "today" },
              { label: "This Week", value: "this_week" },
              { label: "This Month", value: "this_month" },
              { label: "Last Month", value: "last_month" },
            ],
          }}
          buttons={[
            {
              label: "Search",
              onClick: handleSearch,
              className:
                "bg-[#9333EA] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-3 rounded-[3.77px] uppercase w-[100px] ",
            },
          ]}
          containerClass="bg-white p-3 sm:p-4 md:p-6 lg:p-[30px] gap-3 md:gap-3 lg:gap-[15px] lg:justify-item grid grid-cols-1 sm:grid-cols-3 md:grid-cols-2 [@media(min-width:1400px)]:flex "
          filterDropdownClass="md:w-full [@media(min-width:1400px)]:w-[240px]"
          searchInputClass="text-gray-600 md:w-full [@media(min-width:1400px)]:w-[352px] [@media(min-width:1460px)]:w-[388px]"
          datePickerClass="gap-2 "
          buttonWrapperClass="justify-start [@media(min-width:1400px)]:justify-end"
        />

        {/* Show NotFound if no data */}
        <div className="bg-white rounded-[8px] border border-[#E5E5E5] mt-7">
          {isGetting ? (
            <div className="flex justify-center items-center min-h-[200px] mt-36">
              <Loader color="#4772F1" />
            </div>
          ) : TableData.length === 0 ? (
            <div className="flex justify-center items-center min-h-[200px] mt-7">
              <NotFound />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto mt-7">
                <div className="min-w-[1000px]">
                  <Table
                    dataSource={TableData}
                    columns={columns}
                    hasSubTable={true}
                    subTableConfig={subTableConfig}
                    rowKey="key"
                  />
                </div>
              </div>
              <div className="my-4 flex justify-center">
                <Pagination
                  current={currentPage}
                  total={pagination.totalDocuments}
                  pageSize={ITEMS_PER_PAGE}
                  onChange={handlePageChange}
                  className="custom-pagination"
                  showSizeChanger={false}
                  hideOnSinglePage={true}
                />
              </div>
            </>

          )}
        </div>

        {/* Confirmation Modal */}
        <SupportRequestDetailsModal
          isOpen={confirmation.isOpen}
          onCancel={cancel}
          onConfirm={confirm}
          supportLogDetails={selectedRecord || {}}
        />


      </div>
    </div>
  );
};

export default SupportLogs;
